import * as React from 'react';
import { debounce } from '@core-front/helper';
export const useOverflowItems = (props) => {
    const { disabled, element, items, } = props;
    const [overflowItemsCount, setOverflowItemsCount] = React.useState(0);
    const itemLabels = items.map((i) => i.text).join(' ');
    const updateLeftoverItemsCount = React.useCallback(() => {
        const el = element.current;
        /* istanbul ignore else */
        if (el) {
            const { scrollWidth, clientWidth } = el;
            if (scrollWidth > (clientWidth + 1)) {
                // querySelectorAll is not very React-y, but a lot simpler than having to maintain all children refs etc.
                const listItems = el.querySelectorAll('li.tabs__nav-item:not(.sr-only)');
                // do not hide last item
                if (listItems.length > 1) {
                    let width = 0;
                    let count = 0;
                    listItems.forEach((item, index) => {
                        if (scrollWidth - clientWidth > width && index < listItems.length) {
                            width += item.clientWidth;
                            count++;
                        }
                    });
                    setOverflowItemsCount((prevCount) => Math.min(prevCount + count, items.length));
                }
            }
        }
    }, [element, items.length]);
    React.useEffect(() => {
        const handleResize = debounce(() => {
            // if count is > 0, the last useEffect hook will take care of updating
            // if count is 0, setting it to 0 does not trigger update so the last hook will not fire
            setOverflowItemsCount((prevCount) => {
                if (prevCount === 0) {
                    updateLeftoverItemsCount();
                }
                return 0;
            });
        }, 100);
        /* istanbul ignore else */
        if (!disabled) {
            window.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.clear();
        };
    }, [disabled, updateLeftoverItemsCount]);
    React.useEffect(() => {
        /* istanbul ignore else */
        if (!disabled) {
            setOverflowItemsCount((prevCount) => {
                if (prevCount === 0) {
                    updateLeftoverItemsCount();
                }
                return 0;
            });
        }
    }, [disabled, itemLabels, updateLeftoverItemsCount]);
    React.useEffect(() => {
        /* istanbul ignore else */
        if (!disabled) {
            window.setTimeout(() => {
                updateLeftoverItemsCount();
            });
        }
    }, [updateLeftoverItemsCount, disabled, overflowItemsCount]);
    const orderedItems = React.useMemo(() => {
        if (!disabled && overflowItemsCount) {
            // move current item out of dropdown so user can see current tab
            const currentIndex = items.findIndex((i) => i.current);
            const visibleItemsCount = items.length - overflowItemsCount;
            if (currentIndex >= visibleItemsCount) {
                const currentItem = items[currentIndex];
                const otherItems = items.filter((i) => !i.current);
                const beforeItems = visibleItemsCount === 0 ? [] : otherItems.slice(0, visibleItemsCount - 1);
                const afterItems = visibleItemsCount === 0 ? otherItems : otherItems.slice(visibleItemsCount - 1, items.length);
                return [
                    ...beforeItems,
                    currentItem,
                    ...afterItems,
                ];
            }
        }
        return items;
    }, [items, disabled, overflowItemsCount]);
    return {
        items: orderedItems,
        overflowItems: overflowItemsCount > 0 ? orderedItems.slice(1).slice(overflowItemsCount * -1) : [],
    };
};
